import type { BondV2RegulatoryAssuranceData } from './bondV2IssuerFactsMapper'

export interface BondV2MetricItem {
    icon: string
    value: string
    label: string
}

export interface BondV2SubscriptionData {
    label: string
    availableUnits: string
    soldLabel: string
    percent: number
}

export interface BondV2YieldHighlight {
    value: string
    label: string
    description: string
}

export interface BondV2IssuerFactItem {
    label: string
    value: string
}

export interface BondV2InvestTotal {
    label: string
    value: string
    positive?: boolean
}

export interface BondV2InvestData {
    units: number
    stepMessage: string
    quickAdds: string[]
    settlementDate: string
    settlementNote: string
    totals: BondV2InvestTotal[]
}

export interface BondV2CashflowTabData {
    interest: string
    principal: string
}

export interface BondV2CashflowSummaryItem {
    value: string
    label: string
    highlight?: boolean
}

export interface BondV2CashflowItem {
    date: string
    interest: string
    principal: string
}

export interface BondV2CashflowTotals {
    interest: string
    principal: string
}

export interface BondV2FundamentalRow {
    label: string
    value: string
}

export interface BondV2FundamentalsData {
    title: string
    rows: BondV2FundamentalRow[]
    aboutTitle: string
    about: string
}

export interface BondV2StrengthRiskItem {
    title: string
    description: string
    iconUrl?: string
    iconLabel?: string
}

export interface BondV2StrengthRiskGroup {
    title: string
    items: BondV2StrengthRiskItem[]
}

export interface BondV2StrengthsRisksData {
    title: string
    strengths?: BondV2StrengthRiskGroup
    risks?: BondV2StrengthRiskGroup
}

export interface BondV2RatingTimelineItem {
    year: string
    title: string
    rating: string
    description: string
    value: number
    tone: 'orange' | 'blue' | 'green' | 'indigo'
}

export interface BondV2RatingDriverItem {
    title: string
    status: string
    tone: 'green' | 'blue' | 'amber' | 'indigo' | 'red'
    description: string
}

export interface BondV2PeerComparisonData {
    title: string
    description?: string
    summaryLabel?: string
    summary?: string
    tags: string[]
    subtext?: string
    metrics: BondV2PeerMetricData[]
}

export interface BondV2PeerMetricData {
    label: string
    value: string
    issuerValue: string
    medianValue: string
    topValue: string
    helper: string
    issuer: number
    median: number
    top: number
}

export interface BondV2IssuerGlanceMetric {
    icon: string
    value: string
    label: string
    tooltip?: string
}

export interface BondV2FinancialTrendDistributionChart {
    title: string
    tooltip: string
    labels: string[]
    values: number[]
    tooltipLabels?: string[]
    legend: Array<{
        label: string
        color: string
    }>
    chartType?: 'doughnut' | 'pie'
}

export interface BondV2FinancialTrendSeriesConfig {
    type: 'bar' | 'line'
    change: string
    changeTone?: 'positive' | 'negative' | 'neutral'
    labels: string[]
    values: number[]
    legend: []
    unitType?: string
}

export interface BondV2FinancialTrendDistributionConfig {
    type: 'doughnut-grid'
    change: string
    changeTone?: 'positive' | 'negative' | 'neutral'
    charts: BondV2FinancialTrendDistributionChart[]
    unitType?: string
}

export type BondV2FinancialTrendConfig = BondV2FinancialTrendSeriesConfig | BondV2FinancialTrendDistributionConfig

export interface BondV2FinancialTrendData {
    title: string
    tabs: string[]
    configs: Record<string, BondV2FinancialTrendConfig>
}

export interface BondV2IssuerGlanceData {
    title: string
    metrics: BondV2IssuerGlanceMetric[]
    financialTrend: BondV2FinancialTrendData | null
}

export interface BondV2CreditRatingIntelligenceData {
    title: string
    description?: string
    currentRating?: string
    currentRatingStatus?: string
    chips: string[]
    rationaleSummary?: {
        label: string
        value: string
    }
    summaryPoints?: {
        label?: string
        items: string[]
    }
    timeline?: {
        label?: string
        items: BondV2RatingTimelineItem[]
    }
    outlook?: {
        label?: string
        value: string
        implicationsLabel?: string
        implications: string[]
        watchForLabel?: string
        watchFor?: string
    }
    drivers?: {
        label?: string
        items: BondV2RatingDriverItem[]
    }
    peerComparison?: BondV2PeerComparisonData
}

export interface BondV2Data {
    issuerName: string
    isin: string
    issuerLogo?: string
    tags: string[]
    metrics: BondV2MetricItem[]
    subscription: BondV2SubscriptionData
    yieldHighlights: BondV2YieldHighlight[]
    issuerSummary: string
    issuerSummaryTitle: string
    issuerFacts: BondV2IssuerFactItem[]
    whyThisBondTitle: string
    whyThisBond: string[]
    invest: BondV2InvestData
    regulatoryAssurance: BondV2RegulatoryAssuranceData | null
    cashflowTabs: BondV2CashflowTabData
    cashflowSummary: BondV2CashflowSummaryItem[]
    cashflows: BondV2CashflowItem[]
    cashflowTotals: BondV2CashflowTotals
    fundamentals: BondV2FundamentalsData
    issuerGlance: BondV2IssuerGlanceData
    strengthsRisks: BondV2StrengthsRisksData | null
    ratingIntelligence: BondV2CreditRatingIntelligenceData | null
    creditRiskMeter?: any
}

export const bondV2Data: BondV2Data = {
    issuerName: '',
    isin: '',
    issuerLogo: '',
    tags: [],
    metrics: [
        {
            icon: '',
            value: 'N/A',
            label: 'YTM'
        },
        {
            icon: '',
            value: 'N/A',
            label: 'Minimum investment'
        },
        {
            icon: '',
            value: 'N/A',
            label: 'Payout'
        },
        {
            icon: '',
            value: 'N/A',
            label: 'Maturity'
        },
        {
            icon: '',
            value: 'N/A',
            label: 'Coupon Rate'
        },
        {
            icon: '',
            value: 'N/A',
            label: 'Face Value'
        }
    ],
    subscription: {
        label: 'Currently subscribed',
        availableUnits: '0 Units available',
        soldLabel: '0% Sold',
        percent: 0
    },
    yieldHighlights: [
        {
            value: 'N/A',
            label: 'YTM vs coupon',
            description: 'yield above stated coupon'
        },
        {
            value: 'N/A',
            label: 'Clean / dirty price',
            description: 'Excluding / including accrued interest'
        }
    ],
    issuerSummary: '',
    issuerSummaryTitle: 'About the issuer',
    issuerFacts: [],
    whyThisBondTitle: 'Key terms',
    whyThisBond: [],
    invest: {
        units: 1,
        stepMessage: '',
        quickAdds: ['+10 Units', '+20 Units', '+50 Units'],
        settlementDate: '',
        settlementNote:
            'Orders placed after 4 PM will be executed on the next business day. Weekends and exchange holidays are skipped.',
        totals: [
            {
                label: 'Price per Unit',
                value: 'N/A'
            },
            {
                label: 'Investment amount',
                value: 'N/A'
            },
            {
                label: 'Total Interest',
                value: 'N/A'
            },
            {
                label: 'Total Receivable',
                value: 'N/A',
                positive: true
            }
        ]
    },
    regulatoryAssurance: null as BondV2RegulatoryAssuranceData | null,
    cashflowTabs: {
        interest: 'Interest Payout',
        principal: 'Principal Paid at Maturity'
    },
    cashflowSummary: [
        {
            value: 'N/A',
            label: 'Principal Invested'
        },
        {
            value: 'N/A',
            label: 'Interest earned'
        },
        {
            value: 'N/A',
            label: 'Final Payout',
            highlight: true
        }
    ],
    cashflows: [],
    cashflowTotals: {
        interest: 'N/A',
        principal: 'N/A'
    },
    fundamentals: {
        title: 'Bond Fundamentals',
        rows: [
            {
                label: 'Issuer name',
                value: ''
            },
            {
                label: 'Debenture trustee',
                value: ''
            },
            {
                label: 'Ownership',
                value: ''
            },
            {
                label: 'Security',
                value: ''
            },
            {
                label: 'Mode of issuance',
                value: ''
            },
            {
                label: 'Issuance date',
                value: ''
            }
        ],
        aboutTitle: 'About The Issuer',
        about: ''
    },
    issuerGlance: {
        title: 'Issuer at a glance',
        metrics: [],
        financialTrend: null as BondV2FinancialTrendData | null
    },
    strengthsRisks: null as BondV2StrengthsRisksData | null,
    ratingIntelligence: null as BondV2CreditRatingIntelligenceData | null,
    creditRiskMeter: undefined
}
